/**
 * Where the author should land once a manuscript import has finished.
 *
 * Split out of chapterFileUpload so the routing decision can be tested without
 * driving the whole dropzone -> convert -> enrich -> submit pipeline.
 *
 * Note on `createBook`: it is `true` when importing into a brand new book, but
 * on the "append chapters to the book I'm editing" path it arrives as
 * `undefined` rather than `false` (partsList renders ChapterUploadModal without
 * the prop). Anything falsy therefore means "existing book".
 *
 * @param {object} params
 * @param {boolean|undefined} params.createBook  truthy when the import created a new book
 * @param {object} params.book                   the book the parts were imported into
 * @param {string} params.contentVersionId       the content version the parts were appended to
 * @returns {string|null} a route to push, or null when there is nothing to navigate to
 */
export const getPostImportRoute = ({createBook, book, contentVersionId}) => {
  if (!book || !book._id) {
    return null;
  }
  if (createBook) {
    // a brand new book has no editing context to return to yet
    return `/books/${book._id}`;
  }
  const importedVersion = (book.content ?? []).find(
    version => version._id === contentVersionId
  );
  return importedVersion
    ? // the chapter overview of the version we just imported into
      `/books/${book._id}/content/v${importedVersion.versionNumber}`
    : // let the content router resolve the latest version for us
      `/books/${book._id}/content`;
};

/**
 * Wraps up a finished import: dismiss the importer, then navigate.
 *
 * Closing matters as much as navigating. `showChapterUpload` lives in redux, so
 * an importer that is only navigated away from stays flagged as open and
 * re-appears by itself the next time the content sidebar mounts - which is why
 * pressing "Edit content" after an import used to land the author back in the
 * import dialog, with cancelling as the only way through to the chapters.
 *
 * @param {object} params
 * @param {boolean|undefined} params.createBook
 * @param {object} params.book
 * @param {string} params.contentVersionId
 * @param {function} [params.onClose] dismisses the importer, when it has one
 * @param {function} params.push      navigates to a route
 * @returns {string|null} the route that was pushed, if any
 */
export const finishImport = ({
  createBook,
  book,
  contentVersionId,
  onClose,
  push
}) => {
  if (onClose) {
    onClose();
  }
  const route = getPostImportRoute({createBook, book, contentVersionId});
  if (route && push) {
    push(route);
  }
  return route;
};
